import * as React from 'react';
import {ButtonCircle} from './button-circle';
import {SVGIcon} from './svg-icon';
import {classes, style} from 'typestyle';
import {colorBrand, colorContrast, colorSlate, colorWhite} from '../theme/color';
import {transitionQuickEase} from '../theme/transition';
import {important, px} from 'csx';
import {shadowSubtle} from '../theme/shadow';
import {animationPulse} from '../theme/animation';
import {observer} from 'mobx-react';
import {Loading} from './loading/loading';
import {mediaDesktop} from "../theme/media";

export interface HookItProps {
  className?: string;
  size?: number;
  onClick?(event: React.MouseEvent<any>): void;
  isSync?: boolean;
  isHookd?: boolean;
  label?: string;
  removeOnly?: boolean;
  reverse?: boolean;
  invert?: boolean;
  isloading?: boolean;
}

@observer
export class HookIt extends React.Component<HookItProps, any> {
  render() {
    const { size = 28, className, isHookd, reverse, isSync, label, removeOnly, invert, onClick, isloading } = this.props;

    const iconSize = Math.floor(size / 2) - 2;
    const fontSize = px((size / 2) - 1);

    const styles = HookIt.styles;
    
    const cz = classes(
      className,
      styles.common,
      removeOnly
        ? styles.actionRemoveOnly
        : styles.actionDefault,
      isHookd
        ? styles.hookd
        : styles.unhookd,
      isHookd && !invert && styles.hookdNormal,
      !isHookd && !invert && styles.unhookdNormal,
      isHookd && invert && styles.hookdInverted,
      !isHookd && invert && styles.unhookdInverted,
      isSync && styles.sync,
      label && (reverse ? styles.withLabelReverse : styles.withLabel),
      isloading && styles.withLoading
    );

    return (
        <div>
        { isloading && (
            <ButtonCircle className={cz} onClick={onClick} size={size}>
              <Loading size={20}></Loading>
            </ButtonCircle>
        )}

        { !isloading && (
            <ButtonCircle className={cz} onClick={onClick} size={size}>
              {label && reverse && (
                  <span style={{ fontSize }}>{label}</span>
              )}
              {!removeOnly && (
                  <SVGIcon.Basket size={iconSize} />
              )}
              {removeOnly && (
                  <SVGIcon.Cross size={iconSize} />
              )}
              {label && !reverse && (
                  <span style={{ fontSize }}>{label}</span>
              )}
            </ButtonCircle>
        )}
        </div>
    );
  }

  static styles = {
    common: style({
      flexShrink: 0,
      boxShadow: shadowSubtle,
      $nest: {
        '& path': {
          transition: `all ${transitionQuickEase}`
        },
        '& svg': {
          transition: `all ${transitionQuickEase}`
        }
      }
    }),
    hookdNormal: style({
      backgroundColor: important(colorContrast.toString()),
    }),
    hookdInverted: style({
      backgroundColor: important(colorSlate.toString()),
    }),
    actionRemoveOnly: style({}),
    actionDefault: style({
      $nest: {
        '& svg': {
          margin: '-1px 0 0 0',
        }
      }
    }),
    hookd: style({
      transition: `background-color ${transitionQuickEase}`,
      $nest: {
        '& g > path:nth-child(1)': {
          stroke: colorWhite.toString(),
        },
        '& g > path:nth-child(2)': {
          fill: colorWhite.toString()
        },
        '& g > g': {
          stroke: colorWhite.toString()
        },
        '&:hover': {
          backgroundColor: important(colorContrast.darken(0.15).toString()),
        },
        '& span': {
          color: colorWhite.toString()
        },
      }
    }),
    unhookdNormal: style({
      backgroundColor: important(colorWhite.toString()),
    }),
    unhookdInverted: style({
      backgroundColor: important(colorSlate.toString()),
    }),
    unhookd: style({
      transition: `background-color ${transitionQuickEase}`,
      $nest: {
        '& g > path:nth-child(1)': {
          stroke: colorBrand.toString(),
        },
        '& g > path:nth-child(2)': {
          fill: colorBrand.toString(),
        },
        '& g > g': {
          stroke: colorBrand.toString(),
        },
        '&:hover': {
          backgroundColor: important(colorBrand.toString())
        },
        '&:hover g > path:nth-child(1)': {
          stroke: colorWhite.toString(),
        },
        '&:hover g > path:nth-child(2)': {
          fill: colorWhite.toString(),
        },
        '&:hover g > g': {
          stroke: colorWhite.toString(),
        },
        '& span': {
          transition: `color ${transitionQuickEase}`,
          color: colorBrand.toString()
        },
        '&:hover span': {
          color: colorWhite.toString()
        }
      }
    }),
    sync: style({
      $nest: {
        '& path': {
          ...animationPulse
        }
      }
    }),
    withLabel: style({
      width: important('auto'),
      padding: '0 1em 0 0.6em',
      $nest: {
        '& span': {
          margin: '0 0 0 0.5em',
          lineHeight: '1em'
        },
      }
    }),
    withLabelReverse: style({
      width: important('auto'),
      padding: '0 0.6em 0 1em',
      $nest: {
        '& span': {
          margin: '0 0.5em 0 0',
          lineHeight: '1em'
        },
      }
    }),
    withLoading: style({
      padding: '0.4em',
      $nest: {
        '& span': {
          margin: '0 0.5em 0 0',
          lineHeight: '1em'
        },
      }
    },
    mediaDesktop({
      width: important('90px'),
      padding: '0 1em 0 1em',
    }))
  };
}